import { Provider } from '@angular/core';
import { OLRVersion } from '@common';
import { DomainId } from '@config';
import { ContentDictionaryMapping } from '../models/content-dictionary-mapping.type';
import { ContentDictionary } from '../models/content-dictionary.type';
import { ContentService } from '../services/content/content.service';
import { CONTENT_DICTIONARY_MAPPING } from '../tokens/content-dictionary-mapping.token';

export function provideContentDictionaryMapping(
  contentDictionaryMapping?: ContentDictionaryMapping,
): Provider {
  return [
    ContentService,
    {
      provide: CONTENT_DICTIONARY_MAPPING,
      useValue: contentDictionaryMapping ?? {},
      multi: true,
    },
  ];
}

export function mergeContentDictionaries<T extends ContentDictionary>(
  ...contentDictionaries: (T | undefined)[]
): T {
  return contentDictionaries.reduce(
    (target: T, source?: T) => ({ ...target, ...(source ?? {}) }),
    {} as T,
  );
}

export function mergeContentDictionaryMappings<T extends ContentDictionary>(
  ...contentDictionaryMappings: ContentDictionaryMapping<T>[]
): ContentDictionaryMapping<T> {
  const olrVersions = [
    ...new Set(
      contentDictionaryMappings.flatMap(
        (contentDictionaryMapping) =>
          Object.keys(contentDictionaryMapping) as OLRVersion[],
      ),
    ),
  ];

  return olrVersions.reduce((mergedByVersion, olrVersion) => {
    const brandToDictionaryMapsForVersion = contentDictionaryMappings
      .map((contentDictionaryMapping) => contentDictionaryMapping[olrVersion])
      .filter(Boolean) as Partial<Record<DomainId, T>>[];

    const domainIdsForVersion: DomainId[] = [
      ...new Set(
        brandToDictionaryMapsForVersion.flatMap(
          (brandMap) => Object.keys(brandMap) as DomainId[],
        ),
      ),
    ];

    const mergedBrandToDictionaryMap = domainIdsForVersion.reduce(
      (mergedByBrand, domainId) => {
        const dictionariesForBrand = brandToDictionaryMapsForVersion.map(
          (brandMap) => brandMap[domainId],
        );

        return {
          ...mergedByBrand,
          [domainId]: mergeContentDictionaries(...dictionariesForBrand),
        };
      },
      {} as Partial<Record<DomainId, T>>,
    );

    return {
      ...mergedByVersion,
      [olrVersion]: mergedBrandToDictionaryMap,
    };
  }, {} as ContentDictionaryMapping<T>);
}
